
import { useState } from "react";
import { Search, Users, Loader2, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useBluejayGroups } from "@/hooks/useBluejayGroups";
import { CreateBluejayGroupDialog } from "@/components/CreateBluejayGroupDialog";

export const BrowserGroupsSection = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { groups, isLoading } = useBluejayGroups(); // Keep hook for now, will rename later
  
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeLabel = (description: string | null) => {
    // Check if this group has a system-generated ID in the description
    if (description && description.includes('[SYS_ID:')) {
      return "System Synced";
    }
    return "Manual";
  };

  const getCleanDescription = (description: string | null) => {
    if (!description) return null;
    // Remove the SYS_ID prefix if it exists
    return description.replace(/^\[SYS_ID:[^\]]+\]\s*/, '');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading groups...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-6">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-black/20"
          />
        </div>
        <CreateBluejayGroupDialog /> {/* Keep component for now, will rename later */}
      </div>

      {filteredGroups.length === 0 ? (
        <div className="text-center py-12 border border-black/12 rounded-md">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? "No groups match your search criteria." : "Get started by creating your first browser group."}
          </p>
          <CreateBluejayGroupDialog /> {/* Keep component for now, will rename later */}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow>
                <TableHead>Group Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.map((group) => (
                <TableRow key={group.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{group.name}</div>
                        {getCleanDescription(group.description) && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {getCleanDescription(group.description)}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      className={getTypeLabel(group.description) === "System Synced" 
                        ? "bg-green-100 text-green-800" 
                        : "bg-orange-100 text-orange-800"
                      }
                    >
                      {getTypeLabel(group.description)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-900">{group.deviceCount}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">
                      {new Date(group.created_at).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="h-8 px-3">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}
    </div>
  );
};
